// @ts-nocheck
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  CheckCircle2,
  MapPin,
  RefreshCcw,
  ClipboardList,
  AlertCircle,
  Home,
  Box,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { FormSelect } from '@/components/shared/FormSelect';
import { useCallback, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import {
  useCreateProject,
  useProjectTypes,
  useUniqueProjectType,
} from '@/hooks/api/useProjects';
import { FormInput } from '@/components/shared/FormInput';
import { useDialogManager } from '@/hooks/useDialogManager';
import { useNavigate } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { VirtualizedList } from '@/components/shared/VirtualizedList';
import { WizardForm } from '@/components/WizardForm';
import { useFormStore } from '@/stores/formStore';
import { ProjectAreaFeature, useMapStore } from '@/stores/mapStore';
import { ProjectSummary } from './ProjectSummaryCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LayerDefinition } from '@packages/database/types';
import { FeatureCard } from '@/components/map/components/FeatureCard';
import { useSelectionStore } from '@/stores/selectionStore';
import { Badge } from '@/components/ui/badge';
import { getPrimaryLabelField } from '@/helpers/layerSchema.helpers';

export const CreateProjectForm = () => {
  // FORM ID for draft saving
  const FORM_ID = 'CREATE_PROJECT';

  // Handle form/dialog state
  const { saveDraft, getDraft, clearDraft } = useFormStore();
  const { closeDialog } = useDialogManager();
  const projectAreaSelection = useSelectionStore((s) => s.projectArea);
  const setProjectArea = useSelectionStore((s) => s.setProjectArea);

  // Handle map interactions
  const { resetInteraction, setInteractionMode, startInteraction } =
    useMapStore();

  // API hooks
  const { createProject, isCreating } = useCreateProject();
  const { projectTypes, error, isLoading } = useProjectTypes();

  const navigate = useNavigate();

  const methods = useForm<any>({
    defaultValues: useMemo(() => {
      return getDraft(FORM_ID)?.data || {};
    }, [getDraft, FORM_ID]),
  });

  const { reset, register, watch, getValues, setValue, trigger } = methods;

  // Watche form values
  const selectedProjectType = watch('projectTypeId');
  const projectBoundary = watch('boundaryExtent');
  const serviceAreaId = watch('serviceAreaId');
  const { projectType } = useUniqueProjectType(selectedProjectType);

  const allowedKeys = projectType?.layers?.map((l) => l.layer.key) ?? [];

  // Filter assets based on selected project type and area
  const filteredFeatures = useMemo(() => {
    if (!projectAreaSelection?.features) return [];

    // No project type restrictions — show everything
    if (allowedKeys.length === 0) return projectAreaSelection.features;

    return projectAreaSelection.features.filter((f) =>
      allowedKeys.includes(f.layerKey),
    );
  }, [projectAreaSelection, allowedKeys]);

  const filteredAssets = filteredFeatures.filter(
    (f) => f.sourceTable === 'assets',
  );
  const filteredPremises = filteredFeatures.filter(
    (f) => f.sourceTable === 'premises',
  );
  const totalCount = filteredFeatures.length;

  useEffect(() => {
    if (projectAreaSelection) {
      setValue('serviceAreaId', projectAreaSelection.serviceAreaId);
      setValue('boundaryExtent', projectAreaSelection.boundaryExtent);
      setValue(
        'assetIds',
        filteredAssets.map((asset) => asset.id),
      );
      setValue(
        'premiseIds',
        filteredPremises.map((premise) => premise.id),
      );
    } else {
      setValue('serviceAreaId', null);
      setValue('boundaryExtent', null);
      setValue('assetIds', []);
      setValue('premiseIds', []);
    }
  }, [projectAreaSelection, setValue]);

  const resetFormAndClose = useCallback(() => {
    reset();
    clearDraft(FORM_ID);
    setProjectArea(null);
    closeDialog();
  }, [reset, clearDraft, FORM_ID, closeDialog]);

  const onSubmit: SubmitHandler<any> = (formData) => {
    const projectCreateData: any = {
      name: formData.name,
      description: formData.description,
      projectNumber: formData.projectNumber,
      startDate: new Date(formData.startDate),
      endDate: new Date(formData.endDate),
      projectTypeId: formData.projectTypeId,
      serviceAreaId: formData.serviceAreaId,
      assetIds: filteredAssets.map((asset) => asset.id),
      premiseIds: filteredPremises.map((premise) => premise.id),
      status: formData.status,
    };

    createProject(projectCreateData, {
      onSuccess: () => {
        resetFormAndClose();
        toast.success('New project created!');
      },
    });
  };

  const featuresByLayer = useMemo(() => {
    return filteredFeatures.reduce(
      (acc, feature) => {
        const key = feature.layerKey;
        if (!acc[key]) acc[key] = [];
        acc[key].push(feature);
        return acc;
      },
      {} as Record<string, ProjectAreaFeature[]>,
    );
  }, [filteredFeatures]);

  // Get the layer definition for each group
  const layerDefs = useMemo(() => {
    return projectType?.layers.reduce(
      (acc, layer) => {
        //@ts-ignore
        acc[layer.layer.key] = layer.layer;
        return acc;
      },
      {} as Record<string, LayerDefinition>,
    );
  }, [projectType?.layers]);

  const handleDrawBoundaryClick = () => {
    // Save current form state
    const currentData = getValues();
    saveDraft(FORM_ID, currentData);

    // Navigate to map
    navigate('/admin/ticket-map');
    startInteraction('selectProjectArea', {
      onComplete: () => {
        resetInteraction();
        navigate(`/admin/project-management?dialog=create-project`);
      },
      onCancel: () => {
        resetInteraction();
      },
    });
    setInteractionMode('selectProjectArea');
  };

  // Step validation functions
  const validateBoundary = async () => {
    if (!projectBoundary || !serviceAreaId) {
      toast.error('Please select a service area on the map');
      return false;
    }
    return true;
  };

  const validateProjectType = async () => {
    const isValid = await trigger('projectTypeId');
    if (!isValid) {
      toast.error('Please select a project type');
      return false;
    }
    return true;
  };

  const validateProjectDetails = async () => {
    const isValid = await trigger([
      'name',
      'projectNumber',
      'startDate',
      'endDate',
    ]);
    if (!isValid) {
      toast.error('Please fill in all required project details');
      return false;
    }
    return true;
  };

  // Wizard steps
  const wizardSteps = [
    {
      id: 'boundary',
      title: 'Define Area',
      // description: 'Select service area',
      validate: validateBoundary,
      content: (
        <div className="space-y-6">
          <Alert>
            <MapPin className="h-4 w-4" />
            <AlertDescription>
              Click the button below to navigate to the map and draw your
              project boundary. Your form data will be saved automatically.
            </AlertDescription>
          </Alert>

          <Button
            type="button"
            onClick={handleDrawBoundaryClick}
            variant={projectBoundary ? 'outline' : 'default'}
            className="w-full h-12 text-base font-medium"
          >
            {projectBoundary ? (
              <>
                <RefreshCcw className="mr-2 h-5 w-5" />
                Reselect Service Area on Map
              </>
            ) : (
              <>
                <MapPin className="mr-2 h-5 w-5" />
                Select Service Area on Map
              </>
            )}
          </Button>

          {/* Boundary confirmation card */}
          {projectBoundary && (
            <Card className="border-border bg-background">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-green-700 font-medium mb-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  Boundary successfully set
                </div>
                <div className="rounded-md bg-card p-3 text-xs font-mono text-foreground border border-border">
                  <div className="grid grid-cols-4 gap-y-1">
                    <span className="text-foreground">Min X:</span>
                    <span>{projectBoundary[0].toFixed(6)}</span>
                    <span className="text-foreground">Min Y:</span>
                    <span>{projectBoundary[1].toFixed(6)}</span>
                    <span className="text-foreground">Max X:</span>
                    <span>{projectBoundary[2].toFixed(6)}</span>
                    <span className="text-foreground">Max Y:</span>
                    <span>{projectBoundary[3].toFixed(6)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {!projectBoundary && (
            <Alert variant="default" className="bg-muted/50">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                You need to draw a boundary before proceeding to the next step.
              </AlertDescription>
            </Alert>
          )}
        </div>
      ),
    },
    {
      id: 'project-type',
      title: 'Project Type',
      description: 'Select the type of work to find matching assets',
      validate: validateProjectType,
      content: (
        <div className="space-y-6">
          <Alert>
            <ClipboardList className="h-4 w-4" />
            <AlertDescription>
              The project type determines which assets will be included based on
              their type.
            </AlertDescription>
          </Alert>

          {error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Failed to load project types. Please try again.
              </AlertDescription>
            </Alert>
          ) : (
            <FormSelect
              name="projectTypeId"
              label="Project Type"
              rules={{ required: 'Project type is required' }}
              disabled={isLoading}
              placeholder={
                isLoading ? 'Loading project types...' : 'Select a project type'
              }
              options={
                projectTypes?.map((pType: any) => ({
                  value: pType.id,
                  label: pType.name,
                })) || []
              }
            />
          )}

          {/* Asset type info */}
          {selectedProjectType && projectType && (
            <Card className="bg-background border-border">
              <CardContent className="p-4">
                <h4 className="font-medium text-sm text-foreground mb-2">
                  Allowed Asset Types
                </h4>
                {allowedKeys.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {allowedKeys.map((type) => (
                      <span
                        key={type}
                        className="px-2 py-1 bg-primary text-primary-foreground rounded text-xs font-medium"
                      >
                        {type}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm font-medium text-foreground">
                    All asset types
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      ),
    },
    {
      id: 'assets-review',
      title: 'Review Assets',
      description: 'Review the assets that match your selection',
      content: (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Selection Review</h4>
              <p className="text-sm text-muted-foreground">
                Found {totalCount} items in selected area
              </p>
            </div>
          </div>

          {/* Content Area */}
          {totalCount === 0 ? (
            <Alert variant="default" className="bg-yellow-50 border-yellow-200">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                No items found matching your project type in this area. You can
                still continue to create the project.
              </AlertDescription>
            </Alert>
          ) : (
            <Tabs defaultValue={Object.keys(featuresByLayer)[0]}>
              <TabsList>
                {Object.entries(featuresByLayer).map(([layerKey, features]) => {
                  const layerDef = layerDefs?.[layerKey];
                  return (
                    <TabsTrigger key={layerKey} value={layerKey}>
                      {layerDef?.displayName ?? layerKey}
                      <Badge variant="secondary" className="ml-2">
                        {features?.length ?? 0}
                      </Badge>
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {Object.entries(featuresByLayer).map(([layerKey, features]) => {
                const layerDef = layerDefs?.[layerKey];
                return (
                  <TabsContent key={layerKey} value={layerKey} className="mt-2">
                    <VirtualizedList
                      items={features}
                      getKey={(f) => f.id}
                      renderItem={(f) => (
                        <FeatureCard feature={f} layerDef={layerDef} />
                      )}
                      height="400px"
                      getLabel={(f) => {
                        const labelField = getPrimaryLabelField(
                          layerDef?.schemaConfig,
                        );
                        return String(
                          f?.properties?.[labelField] ?? f.id ?? '',
                        );
                      }}
                      rowHeight={80}
                      isSelectable={false}
                    />
                  </TabsContent>
                );
              })}
            </Tabs>
          )}

          {/* Summary Footer */}
          {totalCount > 0 && (
            <Card className="bg-muted/50">
              <CardContent className="p-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground block text-xs uppercase tracking-wider">
                      Assets
                    </span>
                    <span className="font-medium text-lg">
                      {filteredAssets.length}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-xs uppercase tracking-wider">
                      Addresses
                    </span>
                    <span className="font-medium text-lg">
                      {filteredPremises.length}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-xs uppercase tracking-wider">
                      Project Type
                    </span>
                    <span className="font-medium truncate block mt-0.5">
                      {projectTypes?.find((p) => p.id === selectedProjectType)
                        ?.name || 'N/A'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      ),
    },
    {
      id: 'project-details',
      title: 'Project Details',
      description: 'Provide project name, timeline, and description',
      validate: validateProjectDetails,
      content: (
        <div className="space-y-6">
          {/* Project Name */}
          <FormInput
            name="name"
            label="Project Name"
            rules={{ required: 'Project name is required' }}
            placeholder="e.g., Westside Fiber Expansion"
          />

          {/* Project Number/Code */}
          <FormInput
            name="projectNumber"
            label="Project Number"
            rules={{ required: 'Project number is required' }}
            placeholder="e.g., P-2024-001"
          />

          {/* Date Range */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormInput
              name="startDate"
              type="date"
              label="Start Date"
              rules={{
                required: 'Start date is required',
              }}
            />
            <FormInput
              name="endDate"
              type="date"
              label="End Date"
              rules={{
                required: 'End date is required',
                validate: (value, formValues) => {
                  if (!formValues.startDate) return true;
                  return new Date(value) >= new Date(formValues.startDate)
                    ? true
                    : 'End date must be after start date';
                },
              }}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Project Description</Label>
            <Textarea
              {...register('description')}
              id="description"
              placeholder="Describe the project goals, scope, or any relevant notes..."
              className="min-h-[120px]"
            />
          </div>
        </div>
      ),
    },
    {
      id: 'summary',
      title: 'Project Summary',
      description: 'Review project details',
      content: <ProjectSummary data={getValues()} projectType={projectType} />,
    },
  ];

  return (
    <WizardForm
      methods={methods}
      onSubmit={onSubmit}
      steps={wizardSteps}
      submitText="Create Project"
      cancelText="Cancel"
      onCancel={resetFormAndClose}
      isSubmitting={isCreating}
      showStepNumbers={true}
      useUrlParams={true}
      urlParamName="step"
    />
  );
};
